import { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faChevronUp } from "@fortawesome/free-solid-svg-icons";

import { SEO } from "../components/SEO";

const faqs = [
  {
    category: "Remote Support",
    items: [
      {
        q: "How does remote support work?",
        a: "When you click the Remote Support link, you'll download a small, temporary agent that lets us securely connect to your device. We can see your screen, diagnose issues, and apply fixes in real time. The session is fully encrypted and you're always in control—you can end the connection at any time.",
      },
      {
        q: "Is it safe to let someone remote into my computer?",
        a: "Yes. We use ScreenConnect, a professional-grade remote access platform with encrypted sessions and no persistent access—meaning once the session ends, there's no ongoing connection to your device. We'll never ask for your passwords.",
      },
      {
        q: "What types of issues can remote support fix?",
        a: "Most common issues are handled remotely: slow performance, virus and malware removal, software installation and configuration, connectivity problems, Windows or macOS troubleshooting, email setup, printer issues, and more. If a problem requires physical hands-on work, we'll let you know upfront.",
      },
      {
        q: "What operating systems do you support?",
        a: "Windows, macOS, and Linux are all supported. For mobile devices (iOS/Android), we can offer guidance and walk you through steps, though full remote control of mobile devices is more limited.",
      },
      {
        q: "How do I pay for remote support?",
        a: "Payment is made securely through our Square payment portal after the session. We'll agree on the scope and cost before we begin—no surprises.",
      },
    ],
  },
  {
    category: "AI Solutions",
    items: [
      {
        q: "What kinds of AI services do you offer?",
        a: "We help organizations design and deploy practical AI solutions: agentic pipelines that automate repetitive engineering tasks, LLM integrations into existing workflows, real-time analytics and anomaly detection, and AI governance frameworks that keep your deployments safe and compliant.",
      },
      {
        q: "Is AI consulting only for large enterprises?",
        a: "Not at all. While we have experience deploying AI at scale across complex environments, we work with businesses of all sizes. Even small teams can benefit from targeted automation—whether that's automating a reporting workflow or integrating an AI assistant into a core process.",
      },
      {
        q: "How do I get started with AI consulting?",
        a: "Reach out via social media or the contact links on this site. We'll start with a conversation about your goals, identify the highest-impact opportunities, and put together a practical plan that fits your timeline and budget.",
      },
      {
        q: "Do you help with responsible AI and governance?",
        a: "Yes—this is a core area of expertise. We help organizations establish AI usage policies, ethical guardrails, data privacy controls, and compliance frameworks to ensure AI tools are deployed safely and in alignment with your business values and risk standards.",
      },
    ],
  },
  {
    category: "Domain & Hosting",
    items: [
      {
        q: "What's included in domain registration?",
        a: "We handle domain search, registration, DNS configuration, and ongoing management. We can also help with domain transfers if you're moving from another registrar.",
      },
      {
        q: "Do you offer website hosting?",
        a: "Yes. We offer shared hosting, premium hosting plans, and can help you select the right tier for your needs—whether you're launching a personal site, a small business presence, or a higher-traffic application.",
      },
      {
        q: "Can you help migrate an existing website?",
        a: "Absolutely. We handle website migrations including file transfers, database migrations, DNS cutover, and SSL setup to minimize downtime during the transition.",
      },
      {
        q: "Do you help with SSL certificates?",
        a: "Yes—SSL setup and renewal is included as part of our hosting support services. A valid SSL certificate is essential for security, SEO, and visitor trust.",
      },
    ],
  },
  {
    category: "General",
    items: [
      {
        q: "How do I pay for services?",
        a: "We accept various forms of payment for your convenience, including credit and debit cards via our Square payment portal. We want the payment process to be as easy as the service itself—just let us know if you have any specific needs and we'll work something out.",
      },
      {
        q: "Do you offer ongoing support contracts?",
        a: "Yes. For clients who need regular IT support, hosting management, or ongoing AI/cloud work, we offer flexible arrangements. Reach out and we'll figure out what makes the most sense for your situation.",
      },
      {
        q: "How do I get in touch?",
        a: "The easiest way is via Bluesky (@TechGuyDom.com) or Mastodon (@TechGuyDom on twit.social). You can also use the Remote Support link to get directly into a session if you have an urgent issue.",
      },
    ],
  },
];

const FaqItem = ({ q, a }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="faq-item" onClick={() => setOpen(!open)}>
      <div className="faq-question">
        <span>{q}</span>
        <FontAwesomeIcon icon={open ? faChevronUp : faChevronDown} className="faq-chevron" />
      </div>
      {open && <div className="faq-answer">{a}</div>}
    </div>
  );
};

export const FAQ = () => {
  return (
    <>
      <SEO
        title="FAQ — Remote Support, AI Consulting & Hosting"
        description="Answers to common questions about TechGuyDom remote IT support, AI consulting, domain & hosting services, payment, and how to get started."
        path="/faq"
      />
      <div className="page-header-section">
        <h1>Frequently Asked Questions</h1>
        <p className="page-header-sub">
          Answers to common questions about remote support, AI consulting,
          hosting, and how to get started.
        </p>
      </div>

      <div className="container faq-section">
        {faqs.map((group, i) => (
          <div key={i} className="faq-group">
            <h4 className="faq-group-title">{group.category}</h4>
            {group.items.map((item, j) => (
              <FaqItem key={j} q={item.q} a={item.a} />
            ))}
          </div>
        ))}
      </div>
    </>
  );
};
